"use client";

import React, { FormEvent, useState } from "react";

const CommercialNewsletterSignup = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!email) {
      setErrorMessage("Please enter your email address.");
      setStatus("error");
      return;
    }

    setStatus("submitting");
    setErrorMessage("");

    try {
      const response = await fetch("/api/newsletter-commercial", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setErrorMessage(data.error || "Something went wrong. Please try again.");
        setStatus("error");
        return;
      }

      setStatus("success");
      setEmail("");
    } catch (error) {
      console.error("Error submitting commercial newsletter form", error);
      setErrorMessage("Something went wrong. Please try again.");
      setStatus("error");
    }
  };

  return (
    <section id="newsletter-signup" className="mt-12 rounded-xl bg-gray-50">
      <div className="p-8 md:p-8 lg:px-16 lg:py-16">
        <div className="mx-auto max-w-lg text-center">
          <h2 className="text-2xl font-bold text-gray-900 md:text-3xl">
            Stay up to date with Gardian Commercial
          </h2>
          <p className="mt-4 text-gray-500">
            Enter your email address and we&apos;ll send you updates about commercial property opportunities in Mackay.
          </p>
        </div>

        <div className="mx-auto mt-8 max-w-xl">
          <form onSubmit={handleSubmit} className="sm:flex sm:gap-4">
            <div className="sm:flex-1">
              <label htmlFor="commercial-newsletter-email" className="sr-only">
                Email
              </label>
              <input
                id="commercial-newsletter-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="Email address"
                className="focus:ring-yellow-400 w-full rounded-md border-gray-200 bg-white p-3 text-gray-700 shadow-sm transition focus:border-white focus:outline-none focus:ring"
                required
              />
            </div>

            <button
              type="submit"
              disabled={status === "submitting"}
              className="focus:ring-yellow-400 group mt-4 flex w-full items-center justify-center gap-2 rounded-md bg-primary px-5 py-3 text-white transition focus:outline-none focus:ring disabled:cursor-not-allowed disabled:opacity-70 sm:mt-0 sm:w-auto"
            >
              <span className="text-sm font-medium">
                {status === "submitting" ? "Submitting..." : "Subscribe"}
              </span>
              <svg
                className="h-5 w-5 rtl:rotate-180"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </button>
          </form>

          {status === "success" && (
            <p className="mt-4 text-sm text-green-600">
              Thank you for subscribing. We&apos;ll be in touch soon.
            </p>
          )}
          {status === "error" && errorMessage && (
            <p className="mt-4 text-sm text-red-600">{errorMessage}</p>
          )}
        </div>
      </div>
    </section>
  );
};

export default CommercialNewsletterSignup;
